import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './NewPatientBanner.css';

const NewPatientBanner = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Check if popup was closed in this session
        const popupClosed = sessionStorage.getItem('newPatientPopupClosed');
        if (popupClosed !== 'true') {
            // Show popup after a short delay for better UX
            const timer = setTimeout(() => {
                setIsVisible(true);
                // Add blur class to specific content sections only
                const contentSections = [
                    '.hero-section',
                    '.about-header-section', 
                    '.service-section',
                    '.faq-section'
                ];
                
                contentSections.forEach(selector => {
                    const element = document.querySelector(selector);
                    if (element) {
                        element.classList.add('popup-content-blur');
                    }
                });
            }, 1000);
            
            return () => clearTimeout(timer);
        }
    }, []);

    const removeBlurEffects = () => {
        // Remove blur class from content sections
        const contentSections = [
            '.hero-section',
            '.about-header-section',
            '.service-section', 
            '.faq-section'
        ];
        
        contentSections.forEach(selector => {
            const element = document.querySelector(selector);
            if (element) {
                element.classList.remove('popup-content-blur');
            }
        });
    };

    const handleClose = () => {
        if (isClosing || isNavigating) return; // Prevent double-clicks
        
        setIsClosing(true);
        
        // Close immediately without delay
        setIsVisible(false);
        sessionStorage.setItem('newPatientPopupClosed', 'true');
        removeBlurEffects();
        setIsClosing(false);
    };

    const handleBecomePatient = () => {
        if (isClosing || isNavigating) return; // Prevent double-clicks
        
        setIsNavigating(true);
        
        // Remove blur effects immediately for smooth transition
        removeBlurEffects();
        
        // Navigate using React Router for seamless transition
        setTimeout(() => {
            sessionStorage.setItem('newPatientPopupClosed', 'true');
            setIsVisible(false);
            navigate('/OfficeInfo/NewPatients');
            setIsNavigating(false);
        }, 150); // Smooth transition delay
    };

    const handleOverlayClick = (e) => {
        // Only close if clicking the overlay itself, not the popup content
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    if (!isVisible) {
        return null;
    }

    return (
        <>
            {/* Modal Overlay */}
            <div 
                className={`modal-overlay ${isClosing ? 'closing' : ''}`} 
                onClick={handleOverlayClick}
            >
                {/* Modal Content */}
                <div 
                    className={`new-patient-popup ${isClosing ? 'closing' : ''} ${isNavigating ? 'navigating' : ''}`} 
                    onClick={(e) => e.stopPropagation()}
                >
                    <button 
                        className="popup-close-btn"
                        onClick={handleClose}
                        disabled={isClosing || isNavigating}
                        aria-label="Close popup"
                        type="button"
                    >
                        ×
                    </button>
                    
                    <div className="popup-content">
                        <div className="popup-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" fill="currentColor"/>
                                <path d="M19 15L20.09 18.26L24 19L20.09 19.74L19 23L17.91 19.74L14 19L17.91 18.26L19 15Z" fill="currentColor"/>
                                <path d="M5 15L6.09 18.26L10 19L6.09 19.74L5 23L3.91 19.74L0 19L3.91 18.26L5 15Z" fill="currentColor"/>
                            </svg>
                        </div>
                        
                        <h2 className="popup-title">
                            Welcome to Atlantic Primary Care!
                        </h2>
                        
                        <p className="popup-description">
                            We're accepting new patients and would love to be your healthcare partner. 
                            Our experienced team provides comprehensive primary care services at our 
                            convenient Hanover location.
                        </p>
                        
                        <div className="popup-buttons">
                            <button 
                                className={`become-patient-btn ${isNavigating ? 'loading' : ''}`}
                                onClick={handleBecomePatient}
                                disabled={isClosing || isNavigating}
                            >
                                {isNavigating ? (
                                    <>
                                        <span className="loading-spinner"></span>
                                        Redirecting...
                                    </>
                                ) : (
                                    'Become a New Patient'
                                )}
                            </button>
                            <button 
                                className={`maybe-later-btn ${isClosing ? 'loading' : ''}`}
                                onClick={handleClose}
                                disabled={isClosing || isNavigating}
                            >
                                {isClosing ? 'Closing...' : 'Existing Patient'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default NewPatientBanner; 